import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle, Sparkles } from "lucide-react";
import HeroMockup from "./HeroMockup";

/**
 * Hero section — Linear/Vercel-style product-first hero.
 *
 * Copy pattern per 2026 landing-page research:
 *   Outcome-first headline · specific audience subhead · one primary CTA
 *   + quieter secondary · trust signal below the CTA · real product mockup
 *   above the fold (right column on desktop, below on mobile).
 *
 * Layout:
 *   - Radial amber wash behind the copy for depth (no gradient text).
 *   - Grid pattern is generated in CSS, not an image — zero payload.
 *   - Big text hierarchy: text-4xl → text-7xl scale with tight leading.
 *
 * Trust signal line uses `mono` (JetBrains Mono) to echo the app UI's
 * industrial aesthetic without introducing new fonts.
 */
export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-primary text-primary-foreground">
      {/* Backdrop layers: radial wash + faint grid. Kept CSS-only. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 55% at 18% 15%, hsl(28 95% 53% / 0.25) 0%, transparent 60%), radial-gradient(45% 50% at 90% 30%, hsl(28 95% 53% / 0.12) 0%, transparent 60%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--primary-foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary-foreground)) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(circle at 50% 30%, black 40%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(circle at 50% 30%, black 40%, transparent 75%)",
        }}
      />

      <div className="relative max-w-[1200px] mx-auto px-5 md:px-8 pt-14 md:pt-24 pb-16 md:pb-28 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-12 lg:gap-14 items-center">
        <div className="reveal">
          <div
            className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 text-accent px-3 py-1 mono text-[11px] uppercase tracking-[0.25em]"
            data-testid="hero-eyebrow"
          >
            <Sparkles className="h-3 w-3" /> Built for field service crews
          </div>
          <h1
            className="mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black leading-[1.02] tracking-tight"
            data-testid="hero-headline"
          >
            Stop chasing paperwork.
            <br />
            <span className="text-accent">Start running the job.</span>
          </h1>
          <p
            className="mt-6 text-base md:text-xl text-primary-foreground/70 max-w-xl leading-relaxed"
            data-testid="hero-subhead"
          >
            JobsBook is the operations OS for pump hire, plumbing, HVAC, cleaning and mobile trades — bookings, staff scheduling, and invoicing (VAT · RCT · cash) in one place.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link
              to="/signup"
              className="group inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3.5 rounded-md font-bold text-sm hover:bg-accent/90 shadow-[0_8px_30px_rgb(244,114,22,0.25)] hover:shadow-[0_12px_40px_rgb(244,114,22,0.4)] transition-all hover:-translate-y-[2px]"
              data-testid="hero-cta-primary"
            >
              Start free trial
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="#product"
              className="inline-flex items-center gap-2 border border-primary-foreground/20 hover:border-primary-foreground/40 px-6 py-3.5 rounded-md font-semibold text-sm hover:bg-primary-foreground/5 transition-all"
              data-testid="hero-cta-secondary"
            >
              <PlayCircle className="h-4 w-4" /> See it in action
            </a>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 mono text-[11px] uppercase tracking-[0.22em] text-primary-foreground/45">
            <span>No card required</span>
            <span className="h-1 w-1 rounded-full bg-primary-foreground/30" />
            <span>7-day full Pro trial</span>
            <span className="h-1 w-1 rounded-full bg-primary-foreground/30" />
            <span>From €29/mo</span>
          </div>
        </div>

        <div className="reveal reveal-delay-1">
          <HeroMockup />
        </div>
      </div>
    </section>
  );
}
